import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import type { ApiKey } from "@/lib/types";
import { Alert, Badge, Button, Card, EmptyState, Field, PageHeader, Select, Skeleton } from "@/components/ui";

interface CreatedKey {
  key: ApiKey;
  secret: string;
  notice: string;
}

export function ApiKeys() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [mode, setMode] = useState("live");
  const [created, setCreated] = useState<CreatedKey | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () => api.get<{ data: ApiKey[] }>("/v1/api-keys"),
  });

  const create = useMutation({
    mutationFn: () => api.post<CreatedKey>("/v1/api-keys", { name, mode }),
    onSuccess: (result) => {
      setCreated(result);
      setName("");
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.remove<void>(`/v1/api-keys/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api-keys"] }),
  });

  return (
    <>
      <PageHeader
        title="Developers"
        subtitle="Server-side keys for your store, ERP or in-house tooling."
      />

      {created && (
        <div className="mb-5">
          <Alert tone="warn">
            <p className="font-medium">{created.notice}</p>
            <code className="mt-2 block break-all rounded-lg bg-white px-3 py-2 font-mono text-xs text-ink-900">
              {created.secret}
            </code>
          </Alert>
        </div>
      )}

      <Card className="mb-5">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">Create a key</h2>
        <div className="grid gap-4 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
          <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="WooCommerce store" />
          <Select
            label="Mode"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            options={[
              { value: "live", label: "Live" },
              { value: "test", label: "Test" },
            ]}
          />
          <Button loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>
            Create key
          </Button>
        </div>
        <p className="mt-3 text-xs text-ink-400">
          Test keys run the same validation and idempotency as live keys against a sandbox. No collection is booked and nothing is billed.
        </p>
      </Card>

      {isLoading ? (
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : !data?.data.length ? (
        <EmptyState title="No API keys" body="Create a key to book shipments straight from your own systems." />
      ) : (
        <div className="ws-card divide-y divide-ink-200">
          {data.data.map((key) => (
            <div key={key.id} className="flex flex-wrap items-center gap-3 px-5 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{key.name}</p>
                  <Badge tone={key.mode === "live" ? "progress" : "neutral"}>{key.mode}</Badge>
                  {key.revoked_at && <Badge tone="bad">revoked</Badge>}
                </div>
                <p className="mt-0.5 font-mono text-xs text-ink-400">{key.prefix}...</p>
              </div>
              <p className="text-xs text-ink-400">
                {key.last_used_at ? `Last used ${dateTime(key.last_used_at)}` : "Never used"}
              </p>
              {!key.revoked_at && (
                <Button size="sm" variant="danger" onClick={() => revoke.mutate(key.id)}>
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Card className="mt-5">
        <h2 className="mb-3 text-lg font-semibold tracking-tight">Quick start</h2>
        <pre className="overflow-x-auto rounded-xl bg-ink-950 p-4 text-xs leading-relaxed text-ink-200">
{`curl -X POST https://app.weship.co.za/v1/quotes \\
  -H "X-API-Key: wsk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "collection": { "suburb": "Lynnwood Ridge", "city": "Pretoria",
                    "province": "Gauteng", "postal_code": "0040",
                    "street": "1 Main Road", "contact_name": "Depot",
                    "phone": "0821234567" },
    "delivery":   { "suburb": "Sea Point", "city": "Cape Town",
                    "province": "Western Cape", "postal_code": "8005",
                    "street": "5 Beach Road", "contact_name": "Customer",
                    "phone": "0839876543" },
    "parcels": [{ "length_mm": 300, "width_mm": 200,
                  "height_mm": 150, "weight_g": 1200 }]
  }'`}
        </pre>
        <p className="mt-3 text-xs text-ink-400">
          Send an Idempotency-Key header on every booking so a retry never creates a second collection.
        </p>
      </Card>
    </>
  );
}
